/**
 * Does changing `bindingType` / `interiorType` break the Project an item sits in?
 *
 * ## Why this exists
 *
 * The server treats both properties as **hard partition keys** when it
 * auto-creates Projects: `GRAY`, `COLOR` and `PHOTO` never share an
 * auto-created Project, and neither do `COIL` and `PERFECT`. The reasoning
 * (server, 2026-10-17c §1) is that changing either is a *re-specification of
 * the physical product* rather than a revision of the work — a greyscale
 * paperback and a colour coil-bound edition are two things you can order,
 * price and ship independently.
 *
 * That rule is right for a machine making thousands of unsupervised
 * decisions. It is wrong to enforce forever: a user may legitimately want a
 * greyscale original and its colour reissue in one Project. So the handoff
 * point is the moment a **user** changes one of these properties on an item
 * that is already in a Project — at that instant the item no longer matches
 * the Project, the user has all the context, and the question is cheap.
 *
 * **Mixed Projects are legal.** Nothing server-side rejects one, and the
 * server will never silently relocate an item because its binding changed.
 * This prompt exists only so the split does not silently fail to happen.
 *
 * ## The whole point is to ask RARELY
 *
 * A prompt that fires when there is no real decision to make is just badge
 * fatigue arriving by a different road. So this returns `prompt: false` —
 * with a reason, for telemetry and for tests — in every case where the
 * question would be noise:
 *
 *   - the item is in no Project, or is the only member                    → nothing to compare against
 *   - the other members already disagree with each other                  → the Project is ALREADY mixed; the
 *                                                                           user has accepted that, and asking
 *                                                                           again on every subsequent toggle is
 *                                                                           pure nagging
 *   - the new value now AGREES with the other members                     → the edit healed the Project
 *   - the item already disagreed before this edit                         → the user's change did not break
 *                                                                           anything; it was already the odd one
 *                                                                           out, and it is not this edit's job to
 *                                                                           re-litigate that
 *
 * What is left is exactly the case worth a question: *a uniform Project that
 * this edit just made non-uniform.*
 *
 * Contract: docs/contracts/inbox/2026-10-17c-interiortype-partition-and-stale-draft-escalation.md §2
 */

/** The two properties the server partitions Projects on. */
export type PartitionProperty = 'bindingType' | 'interiorType';

/**
 * A Book or Draft sharing the Project. Deliberately structural rather than
 * the `Book` / `Draft` model classes: both carry these fields, and keeping it
 * loose is what lets the smoke test build members without Angular.
 */
export interface PartitionMember {
  bindingType?: string;
  interiorType?: string;
  /** `'TRUE'` / `'FALSE'` string, as the server spells booleans. */
  archived?: string;
}

export type PartitionPromptReason =
  /** The value did not actually change. */
  | 'NO_CHANGE'
  /** The item is in no Project (`otherMembers` was null). */
  | 'NO_PROJECT'
  /** The item is the only active member of its Project. */
  | 'SINGLETON'
  /** The other members already disagree among themselves. */
  | 'OTHERS_MIXED'
  /** The item already differed from the Project before this edit. */
  | 'ALREADY_MISMATCHED'
  /** The new value agrees with the Project. */
  | 'STILL_MATCHES'
  /** This edit made a uniform Project non-uniform. Ask. */
  | 'MISMATCH';

export interface PartitionPromptDecision {
  prompt: boolean;
  reason: PartitionPromptReason;
  property: PartitionProperty;
  /** Normalized value the item now has. */
  newValue: string;
  /**
   * Normalized value every OTHER active member shares, or `''` when there is
   * no single such value (no members, or they disagree).
   */
  projectValue: string;
}

/**
 * Upper-case and trim. Values arrive from several legacy paths and
 * `'gray'` / `'GRAY '` / `'GRAY'` are the same product.
 */
export function normalizePartitionValue(value: string): string {
  if (value === null || value === undefined) {
    return '';
  }
  return ('' + value).trim().toUpperCase();
}

/** `archived` is a string, and absent means not archived. */
export function isArchivedMember(member: PartitionMember): boolean {
  return !!member && normalizePartitionValue(member.archived) === 'TRUE';
}

/**
 * The value `members` agree on, or `''` if there is no agreement.
 *
 * Members whose value is blank are **skipped rather than counted as a
 * distinct value**: an unset binding is missing data, not a third product,
 * and letting it read as disagreement would suppress the prompt exactly when
 * the data is worst.
 */
export function sharedPartitionValue(members: PartitionMember[], property: PartitionProperty): string {
  let shared = '';
  for (const member of members || []) {
    const value = normalizePartitionValue(member ? member[property] : '');
    if (!value) {
      continue;
    }
    if (!shared) {
      shared = value;
    } else if (shared !== value) {
      return '';
    }
  }
  return shared;
}

/**
 * Decide whether to ask the user about a binding / interior change.
 *
 * @param property       which partition key changed
 * @param previousValue  the value before the edit
 * @param newValue       the value after the edit
 * @param otherMembers   every OTHER Book and Draft in the Project — the
 *                       changed item itself must NOT be included — or `null`
 *                       when the item is in no Project
 */
export function evaluatePartitionPrompt(
  property: PartitionProperty,
  previousValue: string,
  newValue: string,
  otherMembers: PartitionMember[] | null
): PartitionPromptDecision {
  const previous = normalizePartitionValue(previousValue);
  const next = normalizePartitionValue(newValue);

  const decision = (prompt: boolean, reason: PartitionPromptReason, projectValue: string): PartitionPromptDecision => {
    return { prompt: prompt, reason: reason, property: property, newValue: next, projectValue: projectValue };
  };

  if (previous === next) {
    return decision(false, 'NO_CHANGE', '');
  }
  if (otherMembers === null || otherMembers === undefined) {
    return decision(false, 'NO_PROJECT', '');
  }

  const active = otherMembers.filter(member => !!member && !isArchivedMember(member));
  if (active.length === 0) {
    return decision(false, 'SINGLETON', '');
  }

  const projectValue = sharedPartitionValue(active, property);
  if (!projectValue) {
    return decision(false, 'OTHERS_MIXED', '');
  }
  if (next === projectValue) {
    return decision(false, 'STILL_MATCHES', projectValue);
  }
  // The item was already the odd one out, so this edit is not what broke the
  // Project. Asking now would make the user answer for a mismatch they did
  // not create, and "keep it here" is already the status quo.
  if (previous !== projectValue) {
    return decision(false, 'ALREADY_MISMATCHED', projectValue);
  }
  return decision(true, 'MISMATCH', projectValue);
}
